/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SupportChatWidget } from "@/components/SupportChatWidget";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Users,
  Target,
  Phone,
  Workflow,
  BookOpen,
  Mic,
  BarChart3,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  HelpCircle,
  Zap,
  MessageSquare,
  Calendar,
  Link as LinkIcon,
  PhoneIncoming,
  PhoneOutgoing,
  Bot,
  GitBranch,
  // New icons for expanded features
  FileAudio,
  Webhook,
  FileText,
  CalendarCheck,
  ScrollText,
  CreditCard,
  LayoutTemplate,
  UserCircle,
  TrendingUp,
  Settings,
  Globe,
  Database,
} from "lucide-react";

export default function HelpPage() {
  const { t } = useTranslation();

  const features = [
    {
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      titleKey: "help.features.agents.title",
      descriptionKey: "help.features.agents.description",
      tipsKey: "help.features.agents.tips",
    },
    {
      icon: Target,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      titleKey: "help.features.campaigns.title",
      descriptionKey: "help.features.campaigns.description",
      tipsKey: "help.features.campaigns.tips",
    },
    {
      icon: Workflow,
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
      titleKey: "help.features.flows.title",
      descriptionKey: "help.features.flows.description",
      tipsKey: "help.features.flows.tips",
    },
    {
      icon: BookOpen,
      color: "text-violet-500",
      bgColor: "bg-violet-500/10",
      titleKey: "help.features.knowledgeBase.title",
      descriptionKey: "help.features.knowledgeBase.description",
      tipsKey: "help.features.knowledgeBase.tips",
    },
    {
      icon: Mic,
      color: "text-pink-500",
      bgColor: "bg-pink-500/10",
      titleKey: "help.features.voices.title",
      descriptionKey: "help.features.voices.description",
      tipsKey: "help.features.voices.tips",
    },
    {
      icon: Phone,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      titleKey: "help.features.phoneNumbers.title",
      descriptionKey: "help.features.phoneNumbers.description",
      tipsKey: "help.features.phoneNumbers.tips",
    },
    // New expanded features
    {
      icon: BarChart3,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      titleKey: "help.features.analytics.title",
      descriptionKey: "help.features.analytics.description",
      tipsKey: "help.features.analytics.tips",
    },
    {
      icon: FileAudio,
      color: "text-rose-500",
      bgColor: "bg-rose-500/10",
      titleKey: "help.features.recordings.title",
      descriptionKey: "help.features.recordings.description",
      tipsKey: "help.features.recordings.tips",
    },
    {
      icon: Webhook,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      titleKey: "help.features.webhooks.title",
      descriptionKey: "help.features.webhooks.description",
      tipsKey: "help.features.webhooks.tips",
    },
    {
      icon: FileText,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      titleKey: "help.features.forms.title",
      descriptionKey: "help.features.forms.description",
      tipsKey: "help.features.forms.tips",
    },
    {
      icon: CalendarCheck,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      titleKey: "help.features.appointments.title",
      descriptionKey: "help.features.appointments.description",
      tipsKey: "help.features.appointments.tips",
    },
    {
      icon: LinkIcon,
      color: "text-teal-500",
      bgColor: "bg-teal-500/10",
      titleKey: "help.features.connections.title",
      descriptionKey: "help.features.connections.description",
      tipsKey: "help.features.connections.tips",
    },
    {
      icon: ScrollText,
      color: "text-slate-500",
      bgColor: "bg-slate-500/10",
      titleKey: "help.features.executionLogs.title",
      descriptionKey: "help.features.executionLogs.description",
      tipsKey: "help.features.executionLogs.tips",
    },
    {
      icon: CreditCard,
      color: "text-lime-500",
      bgColor: "bg-lime-500/10",
      titleKey: "help.features.billing.title",
      descriptionKey: "help.features.billing.description",
      tipsKey: "help.features.billing.tips",
    },
    {
      icon: LayoutTemplate,
      color: "text-fuchsia-500",
      bgColor: "bg-fuchsia-500/10",
      titleKey: "help.features.templates.title",
      descriptionKey: "help.features.templates.description",
      tipsKey: "help.features.templates.tips",
    },
    {
      icon: UserCircle,
      color: "text-sky-500",
      bgColor: "bg-sky-500/10",
      titleKey: "help.features.contacts.title",
      descriptionKey: "help.features.contacts.description",
      tipsKey: "help.features.contacts.tips",
    },
  ];

  const faqs = [
    { questionKey: "help.faq.q1.question", answerKey: "help.faq.q1.answer" },
    { questionKey: "help.faq.q2.question", answerKey: "help.faq.q2.answer" },
    { questionKey: "help.faq.q3.question", answerKey: "help.faq.q3.answer" },
    { questionKey: "help.faq.q4.question", answerKey: "help.faq.q4.answer" },
    { questionKey: "help.faq.q5.question", answerKey: "help.faq.q5.answer" },
    { questionKey: "help.faq.q6.question", answerKey: "help.faq.q6.answer" },
    { questionKey: "help.faq.q7.question", answerKey: "help.faq.q7.answer" },
    { questionKey: "help.faq.q8.question", answerKey: "help.faq.q8.answer" },
    { questionKey: "help.faq.q9.question", answerKey: "help.faq.q9.answer" },
    { questionKey: "help.faq.q10.question", answerKey: "help.faq.q10.answer" },
    { questionKey: "help.faq.q11.question", answerKey: "help.faq.q11.answer" },
    { questionKey: "help.faq.q12.question", answerKey: "help.faq.q12.answer" },
    { questionKey: "help.faq.q13.question", answerKey: "help.faq.q13.answer" },
    { questionKey: "help.faq.q14.question", answerKey: "help.faq.q14.answer" },
    { questionKey: "help.faq.q15.question", answerKey: "help.faq.q15.answer" },
    // AI and technology FAQs
    { questionKey: "help.faq.q16.question", answerKey: "help.faq.q16.answer" },
    { questionKey: "help.faq.q17.question", answerKey: "help.faq.q17.answer" },
    { questionKey: "help.faq.q18.question", answerKey: "help.faq.q18.answer" },
    { questionKey: "help.faq.q19.question", answerKey: "help.faq.q19.answer" },
    { questionKey: "help.faq.q20.question", answerKey: "help.faq.q20.answer" },
    { questionKey: "help.faq.q21.question", answerKey: "help.faq.q21.answer" },
    { questionKey: "help.faq.q22.question", answerKey: "help.faq.q22.answer" },
    { questionKey: "help.faq.q23.question", answerKey: "help.faq.q23.answer" },
    { questionKey: "help.faq.q24.question", answerKey: "help.faq.q24.answer" },
    { questionKey: "help.faq.q25.question", answerKey: "help.faq.q25.answer" },
    { questionKey: "help.faq.q26.question", answerKey: "help.faq.q26.answer" },
    { questionKey: "help.faq.q27.question", answerKey: "help.faq.q27.answer" },
    { questionKey: "help.faq.q28.question", answerKey: "help.faq.q28.answer" },
    // Troubleshooting FAQs
    { questionKey: "help.troubleshooting.agentNotAnswering.question", answerKey: "help.troubleshooting.agentNotAnswering.answer" },
    { questionKey: "help.troubleshooting.poorAudioQuality.question", answerKey: "help.troubleshooting.poorAudioQuality.answer" },
    { questionKey: "help.troubleshooting.agentNotUsingKnowledgeBase.question", answerKey: "help.troubleshooting.agentNotUsingKnowledgeBase.answer" },
    { questionKey: "help.troubleshooting.webhooksNotFiring.question", answerKey: "help.troubleshooting.webhooksNotFiring.answer" },
    { questionKey: "help.troubleshooting.callsNotStarting.question", answerKey: "help.troubleshooting.callsNotStarting.answer" },
  ];

  return (
    <div
      className="space-y-8 pb-8"
      role="main"
      itemScope
      itemType="https://schema.org/HelpPage"
    >
      {/* Header */}
      <header aria-label="Help Page Header">
        <h1
          className="text-3xl font-bold tracking-tight flex items-center gap-3"
          itemProp="name"
        >
          <HelpCircle className="h-8 w-8 text-teal-500" />
          {t("help.title")}
        </h1>
        <p className="text-muted-foreground mt-2 text-lg hero-description" itemProp="description">
          {t("help.subtitle")}
        </p>
      </header>

      {/* Visual Getting Started - Two Paths - Structured as HowTo for AEO */}
      <section
        aria-label="Getting Started Guide"
        itemScope
        itemType="https://schema.org/HowTo"
        className="key-features"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl" itemProp="name">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              {t("help.gettingStarted.title")}
            </CardTitle>
            <CardDescription itemProp="description">
              {t("help.gettingStarted.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
          {/* Step 1 - Common Starting Point */}
          <div
            className="flex flex-col items-center mb-8"
            itemScope
            itemProp="step"
            itemType="https://schema.org/HowToStep"
          >
            <meta itemProp="position" content="1" />
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <Phone className="h-10 w-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold shadow-md">
                1
              </div>
            </div>
            <h3 className="mt-4 font-bold text-lg" itemProp="name">{t("help.workflow.buyNumber.title")}</h3>
            <p className="text-sm text-muted-foreground text-center max-w-xs mt-1" itemProp="text">
              {t("help.workflow.buyNumber.description")}
            </p>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center mb-6">
            <ArrowDown className="h-8 w-8 text-muted-foreground/50" />
          </div>

          {/* Choose Your Path Label */}
          <div className="flex justify-center mb-6">
            <Badge variant="outline" className="text-sm px-4 py-1 bg-muted/50">
              <GitBranch className="h-4 w-4 mr-2" />
              {t("help.workflow.choosePath")}
            </Badge>
          </div>

          {/* Two Paths */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* Path 1: Flow-Based Agent */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10">
                <Badge className="bg-indigo-500 hover:bg-indigo-500 text-white shadow-md">
                  <Workflow className="h-3 w-3 mr-1" />
                  {t("help.workflow.flowPath.badge")}
                </Badge>
              </div>
              <div className="border-2 border-indigo-200 dark:border-indigo-900 rounded-2xl p-6 pt-8 bg-gradient-to-b from-indigo-50/50 to-transparent dark:from-indigo-950/20">
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-6 text-center">
                  {t("help.workflow.flowPath.description")}
                </p>

                {/* Flow Path Steps */}
                <div className="space-y-4">
                  {/* Step 2a: Create Flow */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-md">
                        <Workflow className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                        2
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.flowPath.step1.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.flowPath.step1.description")}</p>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <ArrowDown className="h-5 w-5 text-indigo-300" />
                  </div>

                  {/* Step 3a: Create Agent */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                        <Bot className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                        3
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.flowPath.step2.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.flowPath.step2.description")}</p>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <ArrowDown className="h-5 w-5 text-indigo-300" />
                  </div>

                  {/* Step 4a: Create Connection */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
                        <LinkIcon className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">
                        4
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.flowPath.step3.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.flowPath.step3.description")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Path 2: Simple Incoming Agent */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10">
                <Badge className="bg-teal-500 hover:bg-teal-500 text-white shadow-md">
                  <PhoneIncoming className="h-3 w-3 mr-1" />
                  {t("help.workflow.simplePath.badge")}
                </Badge>
              </div>
              <div className="border-2 border-teal-200 dark:border-teal-900 rounded-2xl p-6 pt-8 bg-gradient-to-b from-teal-50/50 to-transparent dark:from-teal-950/20">
                <p className="text-xs text-teal-600 dark:text-teal-400 font-medium mb-6 text-center">
                  {t("help.workflow.simplePath.description")}
                </p>

                {/* Simple Path Steps */}
                <div className="space-y-4">
                  {/* Step 2b: Create Agent */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                        <Users className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                        2
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.simplePath.step1.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.simplePath.step1.description")}</p>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <ArrowDown className="h-5 w-5 text-teal-300" />
                  </div>

                  {/* Step 3b: Create Connection */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
                        <LinkIcon className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs font-bold">
                        3
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.simplePath.step2.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.simplePath.step2.description")}</p>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <ArrowDown className="h-5 w-5 text-teal-300" />
                  </div>

                  {/* Step 4b: Ready */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
                        <CheckCircle2 className="h-7 w-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                        4
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{t("help.workflow.simplePath.step3.title")}</h4>
                      <p className="text-xs text-muted-foreground">{t("help.workflow.simplePath.step3.description")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Optional: Outbound Campaigns */}
          <div className="mt-10 pt-8 border-t border-dashed">
            <div className="flex justify-center mb-4">
              <Badge variant="outline" className="text-sm px-4 py-1">
                <PhoneOutgoing className="h-4 w-4 mr-2" />
                {t("help.workflow.outbound.badge")}
              </Badge>
            </div>
            <div className="max-w-md mx-auto">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900">
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-md">
                    <Target className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm">{t("help.workflow.outbound.title")}</h4>
                  <p className="text-xs text-muted-foreground">{t("help.workflow.outbound.description")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Final Step: Analytics */}
          <div
            className="mt-8 flex flex-col items-center"
            itemScope
            itemProp="step"
            itemType="https://schema.org/HowToStep"
          >
            <meta itemProp="position" content="5" />
            <div className="flex justify-center mb-4">
              <ArrowDown className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
            </div>
            <h3 className="mt-3 font-bold" itemProp="name">{t("help.workflow.analytics.title")}</h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm mt-1" itemProp="text">
              {t("help.workflow.analytics.description")}
            </p>
          </div>
          </CardContent>
        </Card>
      </section>

      {/* Features Overview - Structured as ItemList for AEO */}
      <section
        aria-label="Features Overview"
        itemScope
        itemType="https://schema.org/ItemList"
        className="key-features"
      >
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2" itemProp="name">
          <Zap className="h-6 w-6 text-amber-500" />
          {t("help.features.title")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="hover:shadow-md transition-shadow"
                itemScope
                itemProp="itemListElement"
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content={String(index + 1)} />
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${feature.bgColor}`}>
                      <Icon className={`h-5 w-5 ${feature.color}`} />
                    </div>
                    <CardTitle className="text-base" itemProp="name">
                      {t(feature.titleKey)}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground" itemProp="description">
                    {t(feature.descriptionKey)}
                  </p>
                  <div className="pt-2 border-t">
                    <p className="text-xs font-medium text-teal-600 dark:text-teal-400 flex items-center gap-1">
                      <Lightbulb className="h-3 w-3" />
                      {t("help.tip")}:
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(feature.tipsKey)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Visual Flows Quick Guide - Structured as ItemList for AEO */}
      <section
        aria-label="Flow Builder Guide"
        itemScope
        itemType="https://schema.org/ItemList"
        className="key-features"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl" itemProp="name">
              <Workflow className="h-5 w-5 text-indigo-500" />
              {t("help.flowBuilder.title")}
            </CardTitle>
            <CardDescription itemProp="description">
              {t("help.flowBuilder.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="1" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.message")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.messageDesc")}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="2" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                  <HelpCircle className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.question")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.questionDesc")}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="3" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.condition")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.conditionDesc")}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="4" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.appointment")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.appointmentDesc")}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="5" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-pink-500 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.transfer")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.transferDesc")}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2" itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
              <meta itemProp="position" content="6" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium" itemProp="name">{t("help.flowBuilder.nodes.end")}</span>
              </div>
              <p className="text-xs text-muted-foreground" itemProp="description">
                {t("help.flowBuilder.nodes.endDesc")}
              </p>
            </div>
          </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ Section - Structured as FAQPage for AEO */}
      <section
        aria-label="Frequently Asked Questions"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              {t("help.faq.title")}
            </CardTitle>
            <CardDescription>
              {t("help.faq.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <AccordionTrigger className="text-left" itemProp="name">
                    {t(faq.questionKey)}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground faq-answer"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <span itemProp="text">{t(faq.answerKey)}</span>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </section>

      {/* Quick Tips - Speakable content for voice search */}
      <section
        aria-label="Quick Tips"
        className="hero-description"
        data-speakable="true"
      >
        <Card className="border-teal-200 dark:border-teal-900 bg-teal-50/50 dark:bg-teal-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-teal-700 dark:text-teal-300">
              <Lightbulb className="h-5 w-5" />
              {t("help.quickTips.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2" itemScope itemType="https://schema.org/ItemList">
              <li
                className="flex items-start gap-2 text-sm text-teal-700 dark:text-teal-300"
                itemScope
                itemProp="itemListElement"
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content="1" />
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span itemProp="name">{t("help.quickTips.tip1")}</span>
              </li>
              <li
                className="flex items-start gap-2 text-sm text-teal-700 dark:text-teal-300"
                itemScope
                itemProp="itemListElement"
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content="2" />
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span itemProp="name">{t("help.quickTips.tip2")}</span>
              </li>
              <li
                className="flex items-start gap-2 text-sm text-teal-700 dark:text-teal-300"
                itemScope
                itemProp="itemListElement"
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content="3" />
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span itemProp="name">{t("help.quickTips.tip3")}</span>
              </li>
              <li
                className="flex items-start gap-2 text-sm text-teal-700 dark:text-teal-300"
                itemScope
                itemProp="itemListElement"
                itemType="https://schema.org/ListItem"
              >
                <meta itemProp="position" content="4" />
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span itemProp="name">{t("help.quickTips.tip4")}</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Support Chat Widget - RAG-powered assistant */}
      <SupportChatWidget />
    </div>
  );
}
